import { useState } from 'react';
import { TableRow, Typography, IconButton, Box, useTheme, Tooltip, CircularProgress } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Edit as EditIcon } from '@mui/icons-material';

import { RowTableCell } from '../../../common/components';
import { useToggleReconciliation } from '../../../hooks/useBankMovements';
import { formatCurrency, formatDate } from '../../bank-movements/utils/formatters';

const ExchangeCheckRow = ({ movement, accountLabel, onEdit, onRefresh }) => {
  const theme = useTheme();
  const toggleReconciliationMutation = useToggleReconciliation();
  const [isEditLoading, setIsEditLoading] = useState(false);

  const amountColor =
    Number(movement.amount ?? 0) >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const bookBalanceColor =
    Number(movement.accumulated_balance ?? 0) >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  const handleToggleReconciliation = async () => {
    try {
      await toggleReconciliationMutation.mutateAsync(movement.uuid);
      onRefresh?.();
    } catch (error) {
      console.error('Error toggling reconciliation:', error);
    }
  };

  const handleEdit = async () => {
    try {
      setIsEditLoading(true);
      await Promise.resolve(onEdit?.(movement));
    } finally {
      setIsEditLoading(false);
    }
  };

  return (
    <TableRow
      sx={{
        backgroundColor: movement.is_reconciled
          ? 'rgba(76, 175, 80, 0.08)'
          : 'rgba(255, 255, 255, 1)',
        borderLeft: movement.is_reconciled
          ? `4px solid ${theme.palette.success.main}`
          : `4px solid ${theme.palette.error.main}`,
      }}
    >
      <RowTableCell>{formatDate(movement.emission_date)}</RowTableCell>
      <RowTableCell>{movement.due_date ? formatDate(movement.due_date) : '-'}</RowTableCell>
      <RowTableCell>{accountLabel}</RowTableCell>
      <RowTableCell>{movement.movement_type_name}</RowTableCell>
      <RowTableCell>{movement.concept || '-'}</RowTableCell>
      <RowTableCell>{movement.voucher_number || '-'}</RowTableCell>
      <RowTableCell>{movement.legal_entity_name || '-'}</RowTableCell>
      <RowTableCell sx={{ textAlign: 'right' }}>
        <Typography color={amountColor} fontFamily="monospace" fontWeight="medium">
          {formatCurrency(movement.amount, 2)}
        </Typography>
      </RowTableCell>
      <RowTableCell sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip
            title={movement.is_reconciled ? 'Marcar como no conciliado' : 'Marcar como conciliado'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={handleToggleReconciliation}
                disabled={toggleReconciliationMutation.isPending}
                sx={{
                  color: movement.is_reconciled
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                }}
              >
                {toggleReconciliationMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : movement.is_reconciled ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Editar movimiento" arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleEdit}
                color="primary"
                disabled={isEditLoading}
              >
                {isEditLoading ? <CircularProgress size={20} /> : <EditIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </RowTableCell>
    </TableRow>
  );
};

export default ExchangeCheckRow;
